import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { ZodError, ZodSchema } from 'zod';
import { SnackBarService } from '../../services/snack-bar.service';
import { XlsxService } from '../../services/xlsx.service';
import { MessageType } from '../../types/snack-bar.types';
import { UploadedFileDetail } from './upload-file.type';

export type FileValidationStatus = {
    name: string;
    valid: boolean;
    error?: ZodError;
};

@Component({
    selector: 'app-upload-file',
    templateUrl: './upload-file.component.html',
    styleUrls: ['./upload-file.component.scss'],
})
export class UploadFileComponent {
    @Input() validationSchema?: ZodSchema;
    @Input() mapFileKeys?: Record<string, string>;
    @Input() fileTabName?: string;
    @Input() parserFunction?: (rows: any[]) => any[];
    @Input() headerRow?: number;
    @Input() multiple = false;

    @Output() rows = new EventEmitter();
    @Output() validationError: EventEmitter<ZodError> = new EventEmitter();
    @Output() clearFiles: EventEmitter<boolean> = new EventEmitter();
    @Output() emitFiles: EventEmitter<NgxFileDropEntry[]> = new EventEmitter();
    @Output() emitFilesDetails: EventEmitter<
        Pick<UploadedFileDetail, 'name' | 'extension'>[]
    > = new EventEmitter();
    @Output() fileRemovedByIndex: EventEmitter<number> = new EventEmitter();

    files: NgxFileDropEntry[] | undefined = undefined;
    fileName?: string[];
    fileValidationStatuses: FileValidationStatus[] = [];

    constructor(
        private xlsxService: XlsxService,
        private readonly snackBarService: SnackBarService
    ) {}

    async getRowsFromFile(files: NgxFileDropEntry[]) {
        if (this.multiple) {
            this.files = this.files ? [...this.files, ...files] : [...files];
            await this.validateFilesIndividually(files);
        } else {
            this.files = files;
            await this.validateSingleBatch(files);
        }
    }

    private async validateSingleBatch(files: NgxFileDropEntry[]) {
        const rows = await this.xlsxService.handleWithUploadedFile(
            files,
            this.mapFileKeys,
            this.fileTabName,
            this.parserFunction,
            this.headerRow
        );
        this.fileName = this.xlsxService.getNamesFromFiles(this.files!);
        this.emitFilesDetails.emit(this.getFilesDetails(this.fileName));

        if (this.validationSchema) {
            try {
                const rowsParsed = this.validationSchema.parse(rows);
                this.rows.emit(rowsParsed);
                this.emitFiles.emit(this.files);
                this.snackBarService.displayMessage(
                    'Sucesso ao importar planilha',
                    MessageType.Success
                );
                return;
            } catch (error) {
                const isZodError = error instanceof ZodError;
                if (isZodError) {
                    console.error(`Zod's parse error:`, error);
                    this.validationError.emit(error);
                    this.snackBarService.displayMessage(
                        'Erro de validação da planilha!',
                        MessageType.Error
                    );
                    return;
                }
                console.error('Error:', error);
                this.snackBarService.displayMessage(
                    'Erro ao importar a planilha!',
                    MessageType.Error
                );
            }
        }

        this.rows.emit(rows);
    }

    private async validateFilesIndividually(newFiles: NgxFileDropEntry[]) {
        this.fileName = this.xlsxService.getNamesFromFiles(this.files!);

        let hasError = false;

        for (const file of newFiles) {
            const fileName = file.fileEntry.name;
            const rows = await this.xlsxService.handleWithUploadedFile(
                [file],
                this.mapFileKeys,
                this.fileTabName,
                this.parserFunction,
                this.headerRow
            );

            if (this.validationSchema) {
                try {
                    this.validationSchema.parse(rows);
                    this.fileValidationStatuses.push({
                        name: fileName,
                        valid: true,
                    });
                } catch (error) {
                    hasError = true;
                    const isZodError = error instanceof ZodError;
                    this.fileValidationStatuses.push({
                        name: fileName,
                        valid: false,
                        error: isZodError ? error : undefined,
                    });
                }
            } else {
                this.fileValidationStatuses.push({
                    name: fileName,
                    valid: true,
                });
            }
        }

        const validFiles = this.files!.filter(
            (_, i) => this.fileValidationStatuses[i]?.valid
        );
        const validFileNames = this.fileValidationStatuses
            .filter((s) => s.valid)
            .map((s) => s.name);

        this.emitFilesDetails.emit(this.getFilesDetails(validFileNames));

        if (hasError) {
            const invalidNames = this.fileValidationStatuses
                .filter((s) => !s.valid)
                .map((s) => s.name);
            this.snackBarService.displayMessage(
                `Erro de validação: ${invalidNames.join(', ')}`,
                MessageType.Error
            );

            const firstError = this.fileValidationStatuses.find(
                (s) => !s.valid && s.error
            );
            if (firstError?.error) {
                this.validationError.emit(firstError.error);
            }
        }

        if (validFiles.length > 0) {
            this.emitFiles.emit(validFiles);
            if (!hasError) {
                this.snackBarService.displayMessage(
                    `${validFiles.length} planilha(s) importada(s) com sucesso`,
                    MessageType.Success
                );
            }
        }
    }

    removeFiles() {
        this.files = undefined;
        this.fileName = undefined;
        this.fileValidationStatuses = [];
        this.clearFiles.emit(true);
    }

    removeFileByIndex(index: number) {
        if (!this.files || !this.fileName) return;

        this.files.splice(index, 1);
        this.fileName.splice(index, 1);
        this.fileValidationStatuses.splice(index, 1);

        if (this.files.length === 0) {
            this.removeFiles();
            return;
        }

        this.emitFilesDetails.emit(this.getFilesDetails(this.fileName));

        const validFiles = this.files.filter(
            (_, i) => this.fileValidationStatuses[i]?.valid
        );
        if (validFiles.length > 0) {
            this.emitFiles.emit(validFiles);
        }
    }

    getFileValidationStatus(index: number): FileValidationStatus | undefined {
        return this.fileValidationStatuses[index];
    }

    getFilesDetails(
        files: string[]
    ): Pick<UploadedFileDetail, 'name' | 'extension'>[] {
        return files.map((file) => ({
            name: file,
            extension: file.split('.').reverse()[0],
        }));
    }
}
